import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { Carrier, Trunk, RateDeck, RateRule, BillingAccount, Movement } from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const billingKeys = {
  carriers: () => ['carriers'] as const,
  trunks: (carrierId?: string) => ['trunks', carrierId ?? 'all'] as const,
  decks: () => ['rate-decks'] as const,
  deckRules: (deckId: string, q?: string) => ['rate-deck-rules', deckId, q ?? ''] as const,
  billing: (tenantId: string) => ['billing', tenantId] as const,
  movements: (tenantId: string) => ['billing-movements', tenantId] as const,
};

export const useCarriers = () =>
  useQuery({
    queryKey: billingKeys.carriers(),
    queryFn: () => api.get<ListResponse<Carrier>>('/v1/carriers'),
  });

export const useTrunks = (carrierId?: string) =>
  useQuery({
    queryKey: billingKeys.trunks(carrierId),
    queryFn: () =>
      api.get<ListResponse<Trunk>>(`/v1/trunks${carrierId ? `?carrier_id=${carrierId}` : ''}`),
    // Carries the switch's live view of each trunk (health, registration),
    // which the agent refreshes every 10 s. Cheap: a handful of rows.
    refetchInterval: 15_000,
  });

export const useRateDecks = () =>
  useQuery({
    queryKey: billingKeys.decks(),
    queryFn: () => api.get<ListResponse<RateDeck>>('/v1/rate-decks'),
  });

export const useRateRules = (deckId: string | null, q: string, version?: number | null) =>
  useQuery({
    queryKey: [...billingKeys.deckRules(deckId ?? '', q), version ?? 'current'],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (version != null) p.set('version', String(version));
      const qs = p.toString();
      return api.get<{ data: RateRule[]; total: number; version: number; is_published: boolean }>(
        `/v1/rate-decks/${deckId}/rules${qs ? `?${qs}` : ''}`);
    },
    enabled: !!deckId,
  });

/** Every version of a sheet, newest first: which is live, and whether a draft is waiting. */
export const useRateDeckVersions = (deckId: string | null) =>
  useQuery({
    queryKey: ['rate-decks', deckId, 'versions'],
    queryFn: () => api.get<{
      data: Array<{
        id: string; version_number: number; is_published: boolean;
        effective_from: string | null; effective_to: string | null; rule_count?: number;
      }>;
    }>(`/v1/rate-decks/${deckId}/versions`),
    enabled: !!deckId,
  });

/**
 * One rate, written into the DRAFT.
 *
 * A published sheet is never changed: calls already invoiced were priced by it, and re-pricing
 * them retrospectively is the one thing a billing system may not do (ADR-015). The API forks a
 * draft from the published sheet, copies every rule into it, and applies the change there; it
 * goes live when the draft is published.
 */
export const useSaveRate = (deckId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: Record<string, unknown>) =>
      api.post<{ version: number; rules_written: number; is_published: boolean }>(
        `/v1/rate-decks/${deckId}/rules`, { rules: [rule], replace: false }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rate-decks'] });
    },
  });
};

/** Take a destination out of the draft. */
export const useRemoveRate = (deckId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { prefix: string; time_band: string }) =>
      api.delete<{ version: number }>(`/v1/rate-decks/${deckId}/rules`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rate-decks'] });
    },
  });
};

/**
 * Make the draft the sheet the switch rates with. Names the checksum its preview showed: the API
 * refuses a publish without one, or for a draft changed since (doc 15 section 3).
 */
export const usePublishRateDeck = (deckId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (previewChecksum: string) => api.post<{
      version: number; effective_from: string; checksum: string; rules: number;
    }>(`/v1/rate-decks/${deckId}/actions/publish`, { preview_checksum: previewChecksum }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rate-decks'] });
    },
  });
};

export interface RateChange {
  prefix: string; description: string | null; time_band: string;
  kind: 'added' | 'removed' | 'raised' | 'lowered' | 'terms'; old_rate: string | null; new_rate: string | null;
}
export interface RatePreview {
  deck: { id: string; name: string; deck_type: 'cost' | 'sell'; currency: string };
  draft_version: number; live_version: number | null; checksum: string; rules: number;
  counts: { added: number; removed: number; raised: number; lowered: number; terms: number; unchanged: number };
  changes: RateChange[];
  impact: {
    days: number; calls: number; unrated: number; currency: string; before: string; after: string; difference: string;
    by_destination: Array<{ prefix: string; description: string | null; calls: number; minutes: number; before: string; after: string; difference: string }>;
  };
  below_cost: { count: number; rules: Array<{ prefix: string; description: string | null; sell: string; cost: string; cost_deck: string }> };
}

/** What publishing the draft would change, and what it would do to revenue (or cost). Never cached. */
export const useRatePreview = (deckId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['rate-decks', deckId, 'preview'],
    queryFn: () => api.get<RatePreview>(`/v1/rate-decks/${deckId}/preview`),
    enabled: !!deckId && enabled,
    staleTime: 0,
    gcTime: 0,
  });

/** A sell sheet's draft rebuilt from a carrier's cost at the markup (D10: 30%, rounded up). */
export const useRebuildRetail = (deckId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { from_deck_id: string; markup_pct: number }) =>
      api.post<{ version: number; rules: number; from_version: number; from_draft: boolean }>(`/v1/rate-decks/${deckId}/actions/rebuild`, b),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rate-decks'] }); },
  });
};

/**
 * A whole carrier sheet into the draft, replacing what was there. The import takes 5,000 rules a
 * call, so a bigger sheet goes in pieces: the first replaces, the rest add.
 */
export const useImportRates = (deckId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: Array<Record<string, unknown>>) => {
      let written = 0;
      for (let i = 0; i < rules.length; i += 5000) {
        const r = await api.post<{ version: number; rules_written: number }>(`/v1/rate-decks/${deckId}/rules`,
          { rules: rules.slice(i, i + 5000), replace: i === 0 });
        written += r.rules_written;
      }
      return { written };
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rate-decks'] }); },
  });
};

export interface RateTestResult {
  ok: boolean;
  charge: string | null;
  currency: string | null;
  billable_seconds: number | null;
  reason: string | null;
  deck_version?: number;
  breakdown: {
    matched_prefix: string; rule_id: string; band: string;
    raw_seconds: number; free_seconds: number; billable_seconds: number;
    rate_per_minute: string; connection_fee: string; minimum_charge_applied: boolean;
  } | null;
}

/**
 * The rate lookup tester. Not cached: the whole point is to answer "what would
 * THIS call cost right now", and a stale answer during a billing dispute is
 * worse than no answer.
 */
export const useRateTest = (deckId: string | null) =>
  useMutation({
    mutationFn: (body: { destination: string; duration_sec: number }) =>
      api.post<RateTestResult>(`/v1/rate-decks/${deckId}/actions/test`, body),
  });

export const useBillingAccount = (tenantId: string | null) =>
  useQuery({
    queryKey: billingKeys.billing(tenantId ?? ''),
    queryFn: () => api.get<BillingAccount>(`/v1/tenants/${tenantId}/billing`),
    enabled: !!tenantId,
    retry: false, // a tenant with no billing account 404s; that is a real answer
  });

export const useMovements = (tenantId: string | null) =>
  useQuery({
    queryKey: billingKeys.movements(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<Movement>>(`/v1/tenants/${tenantId}/billing/movements`),
    enabled: !!tenantId,
  });

export function useAdjustBalance(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: string; kind: string; reason: string }) =>
      api.post<{ balance: string }>(`/v1/tenants/${tenantId}/billing/actions/adjust`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) });
      void qc.invalidateQueries({ queryKey: billingKeys.movements(tenantId) });
    },
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/tenants', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tenants'] }); },
  });
}

/** The account's call plan, and with it the rate sheet its calls are priced on. Admin addresses only. */
export function useSetCallPlan(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callPlanId: string | null) =>
      api.post<{ call_plan_id: string | null; sell_rate_deck_id: string | null }>(
        `/v1/tenants/${tenantId}/billing/actions/set-plan`, { call_plan_id: callPlanId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) });
      void qc.invalidateQueries({ queryKey: ['call-plans'] });
    },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch(`/v1/tenants/${id}`, body),
    // The list and the account's own page both show what was changed, so both are refetched.
    onSuccess: (_r, { id }) => {
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['tenant', id] });
    },
  });
}

// ---- account modules (0130): the dialler and Auto QA, switched by the provider

export function useTenantModules(tenantId: string) {
  return useQuery({
    queryKey: ['tenant-modules', tenantId],
    queryFn: () => api.get<{ data: import('@arcnet/contracts').AccountModule[] }>(`/v1/tenants/${tenantId}/modules`),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

export function useSetModule(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ module, enabled }: { module: string; enabled: boolean }) =>
      api.put<{ ok: true; campaigns_paused: number }>(`/v1/tenants/${tenantId}/modules/${module}`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-modules', tenantId] });
      void qc.invalidateQueries({ queryKey: ['dialler', tenantId] });
    },
  });
}
